import { useTimeZone } from '@/auth/useTimeZone.js';
import { StudentStatusBadge } from '@/components/students/student-status-badge.js';
import { EntityTable } from '@/components/table/entity-table.js';
import { TableCellWithPopover } from '@/components/table/table-cell-with-popover.js';
import { TableErrorState } from '@/components/table/table-error-state.js';
import { TableFilterAppliedPlainDateRangePicker } from '@/components/table/table-filter-applied-plain-date-range-picker.js';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown.js';
import { TableFilterInputField } from '@/components/table/table-filter-field.js';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { EmptyArray, getSchoolPortalStudents } from '@/lib/api-calls.js';
import { buildClientExportColumns, exportToCSV } from '@/lib/csv-utils.js';
import { useErrorToast } from '@/lib/error-handling.js';
import { usePaginatedFilters } from '@/lib/filtering.js';
import { useNavigateSearchState } from '@/lib/routing.js';
import { useSorting } from '@/lib/sorting.js';
import { getNowPlainDateISO } from '@/lib/time-zones.js';
import {
  calculateAttendanceRate,
  formatAttendedSessionDatesFromFeedback,
  formatAttendedSessionsInDateRange,
  getStudentStatusLabel,
  getStudentStatusOptions,
  getStudentYearOptions,
} from '@/lib/student-utils.js';
import { airColumnHelper } from '@/lib/tables.js';
import { formatStudentClassTimeAbbr } from '@/routes/_layout/scheduling/schedules/-components/formatting.js';
import { CsvCellValue } from '@airreading/common';
import {
  DEPRECATED_allPagesModifier,
  getSchoolPortalStudentIdDisplay,
  GetSchoolPortalStudentsSortableColumn,
  getSchoolPortalStudentsSortableColumnSchema,
  SchoolPortalStudent,
  StudentStatus,
  StudentYearColorMap,
  studentYearOption,
} from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { LoaderIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

const schoolStudentsRoute = getRouteApi('/_layout/school-students/');

const columnHelper = airColumnHelper<SchoolPortalStudent>(getSchoolPortalStudentsSortableColumnSchema);

const dualLineHeader = (title: string, subtitle: string) => (
  <div className="flex flex-col">
    <span>{title}</span>
    <span className="text-xs font-normal text-muted-foreground">{subtitle}</span>
  </div>
);

const dataColumns = (timeZone: string, attendedStartDate?: string, attendedEndDate?: string) => [
  columnHelper.accessor('firstName', {
    id: 'firstName',
    header: 'First name',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('lastName', {
    id: 'lastName',
    header: 'Last name',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    cell: ({ getValue }) => {
      const status = getValue();
      return status ? <StudentStatusBadge status={status} /> : '-';
    },
  }),
  columnHelper.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
          {studentYearOption(grade)}
        </span>
      );
    },
  }),
  columnHelper.accessor('requestedClassTime', {
    id: 'requestedClassTime',
    header: 'Class time',
    cell: ({ getValue }) => {
      const classTime = getValue();
      if (!classTime) return '-';
      return <Badge variant="outline">{formatStudentClassTimeAbbr(classTime)}</Badge>;
    },
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendedSessionsOverall',
    header: () => dualLineHeader('Attended sessions', 'Overall'),
    cell: ({ getValue }) => {
      const sessionFeedbacks = getValue();
      return (
        sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
          ?.length ?? 0
      );
    },
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendedSessionsSelectedDates',
    header: () => dualLineHeader('Attended sessions', 'Selected dates'),
    cell: ({ getValue }) => {
      return formatAttendedSessionsInDateRange(getValue(), timeZone, attendedStartDate, attendedEndDate);
    },
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendancePercentOverall',
    header: () => dualLineHeader('Attendance %', 'Overall'),
    cell: ({ getValue }) => `${calculateAttendanceRate(getValue())}%`,
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'attendancePercentPast14Days',
    header: () => dualLineHeader('Attendance %', 'Past 14 days'),
    cell: ({ getValue }) => `${calculateAttendanceRate(getValue(), 14)}%`,
  }),
  columnHelper.accessor('classroomTeacher', {
    id: 'classroomTeacher',
    header: 'In-room staff',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor((row) => getSchoolPortalStudentIdDisplay(row), {
    id: 'studentId',
    header: 'Student ID',
    cell: ({ getValue }) => getValue() || '-',
  }),
  columnHelper.accessor('sessionFeedbacks', {
    id: 'all-attended-dates',
    header: 'All attended dates',
    cell: ({ getValue }) => {
      const datesString = formatAttendedSessionDatesFromFeedback(getValue(), timeZone);
      if (!datesString) return '-';

      const dates = datesString.split(', ').filter((date) => date.trim() !== '');
      return <TableCellWithPopover items={dates} />;
    },
  }),
];

const csvHeaderOverrides: Record<string, string> = {
  attendedSessionsOverall: 'Attended sessions (Overall)',
  attendedSessionsSelectedDates: 'Attended sessions - selected dates',
  attendancePercentOverall: 'Attendance % (Overall)',
  attendancePercentPast14Days: 'Attendance % (Past 14 days)',
  'all-attended-dates': 'All attended dates',
};

const csvColumnOverrides = (
  timeZone: string,
  attendedStartDate?: string,
  attendedEndDate?: string,
): Record<string, (row: SchoolPortalStudent, index: number) => CsvCellValue> => ({
  status: (row) => getStudentStatusLabel(row.status),
  grade: (row) => studentYearOption(row.grade),
  requestedClassTime: (row) => (row.requestedClassTime ? formatStudentClassTimeAbbr(row.requestedClassTime) : ''),
  attendedSessionsOverall: (row) =>
    row.sessionFeedbacks?.filter((feedback) => feedback.studentAttended === true && feedback.status === 'completed')
      ?.length ?? 0,
  attendedSessionsSelectedDates: (row) => {
    return formatAttendedSessionsInDateRange(row.sessionFeedbacks, timeZone, attendedStartDate, attendedEndDate);
  },
  attendancePercentOverall: (row) => `${calculateAttendanceRate(row.sessionFeedbacks)}%`,
  attendancePercentPast14Days: (row) => `${calculateAttendanceRate(row.sessionFeedbacks, 14)}%`,
  studentId: (row) => getSchoolPortalStudentIdDisplay(row) ?? '',
  'all-attended-dates': (row) => formatAttendedSessionDatesFromFeedback(row.sessionFeedbacks, timeZone) || '',
});

export interface SchoolStudentsTableProps {
  schoolName?: string;
}

export function SchoolStudentsTable({ schoolName }: SchoolStudentsTableProps) {
  const { timeZone } = useTimeZone();
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  const navigate = schoolStudentsRoute.useNavigate();
  const { attendedStartDate, attendedEndDate } = schoolStudentsRoute.useSearch();

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userQuery, activeUserQuery], setUserQuery],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      classTimeIds: [[selectedClassTimeIds, activeClassTimeIds], setSelectedClassTimeIds],
      classroomTeachers: [[selectedClassroomTeachers, activeClassroomTeachers], setSelectedClassroomTeachers],
    },
    resetFilters,
  } = usePaginatedFilters(schoolStudentsRoute.id, ['query', 'grades', 'statuses', 'classTimeIds', 'classroomTeachers']);

  const setAttendedDateRange = useCallback(
    (range: { fromPlainDateString?: string; toPlainDateString?: string }) => {
      navigate({
        search: (prev) => ({
          ...prev,
          attendedStartDate: range.fromPlainDateString,
          attendedEndDate: range.toPlainDateString,
          pageIndex: 0,
        }),
      });
    },
    [navigate],
  );

  const handleResetFilters = useCallback(() => {
    resetFilters();
    navigate({
      search: (prev) => ({
        ...prev,
        attendedStartDate: undefined,
        attendedEndDate: undefined,
        pageIndex: 0,
      }),
    });
  }, [navigate, resetFilters]);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(schoolStudentsRoute.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetSchoolPortalStudentsSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);
  const effectiveSortBy = sortBy ?? ['attendancePercentOverall-asc'];

  const filters = useMemo(
    () => ({
      query: activeUserQuery || undefined,
      grades: activeSelectedGrades,
      statuses: activeSelectedStatuses,
      classTimeIds: activeClassTimeIds,
      classroomTeachers: activeClassroomTeachers,
      attendedStartDate: attendedStartDate || undefined,
      attendedEndDate: attendedEndDate || undefined,
      timeZone,
    }),
    [
      activeUserQuery,
      activeSelectedGrades,
      activeSelectedStatuses,
      activeClassTimeIds,
      activeClassroomTeachers,
      attendedStartDate,
      attendedEndDate,
      timeZone,
    ],
  );

  const { data, isPending, error, refetch } = useQuery(
    getSchoolPortalStudents({
      pageIndex,
      pageSize,
      filters,
      sortBy: effectiveSortBy,
    }),
  );
  useErrorToast(error, 'Failed to load students');

  const { refetch: refetchAllStudents } = useQuery({
    ...getSchoolPortalStudents({
      ...DEPRECATED_allPagesModifier,
      filters,
      sortBy: effectiveSortBy,
    }),
    enabled: false,
  });

  const students = data?.students ?? EmptyArray;
  const classTimeOptions = useMemo(
    () => data?.filterOptions.classTimes.map(({ id, label }) => ({ value: id, label })) ?? EmptyArray,
    [data?.filterOptions.classTimes],
  );
  const classroomTeacherOptions = useMemo(
    () => data?.filterOptions.classroomTeachers.map((teacher) => ({ value: teacher, label: teacher })) ?? EmptyArray,
    [data?.filterOptions.classroomTeachers],
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'rowNumber',
        header: '',
        cell: ({ row }) => pageIndex * pageSize + row.index + 1,
      }),
      ...dataColumns(timeZone, attendedStartDate, attendedEndDate),
    ],
    [pageIndex, pageSize, timeZone, attendedStartDate, attendedEndDate],
  );

  const table = useReactTable({
    data: students,
    columns,
    state: { pagination: { pageIndex, pageSize }, sorting },
    rowCount: data?.total ?? 0,
    onPaginationChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    ...tableSortingOptions,
  });

  const exportColumns = useMemo(
    () =>
      buildClientExportColumns(
        dataColumns(timeZone, attendedStartDate, attendedEndDate),
        csvColumnOverrides(timeZone, attendedStartDate, attendedEndDate),
      ).map((col) => ({
        ...col,
        id: csvHeaderOverrides[col.id] ?? col.id,
      })),
    [timeZone, attendedStartDate, attendedEndDate],
  );

  const handleDownloadCsv = useCallback(async () => {
    setIsDownloadingCsv(true);
    try {
      const result = await refetchAllStudents();
      const allStudents = result.data?.students ?? [];
      const date = getNowPlainDateISO(timeZone).toString();
      const filename = schoolName
        ? `${schoolName.replace(/ /g, '_')}_students_export_${date}.csv`
        : `students_export_${date}.csv`;
      exportToCSV(allStudents, exportColumns, filename);
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [refetchAllStudents, exportColumns, schoolName, timeZone]);

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name, email or state ID"
          value={userQuery ?? ''}
          onChange={(event) => setUserQuery(event.target.value)}
        />
        <TableFilterDropdown
          title="Grades"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Status"
          options={getStudentStatusOptions().filter((status) => status.value !== StudentStatus.program_concluded)}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterDropdown
          title="Class time"
          options={classTimeOptions}
          selectedValues={selectedClassTimeIds ?? []}
          onChange={setSelectedClassTimeIds}
        />
        <TableFilterDropdown
          title="In-room staff"
          options={classroomTeacherOptions}
          selectedValues={selectedClassroomTeachers ?? []}
          onChange={setSelectedClassroomTeachers}
        />
        <TableFilterAppliedPlainDateRangePicker
          fromPlainDateString={attendedStartDate}
          toPlainDateString={attendedEndDate}
          onRangeApply={setAttendedDateRange}
          placeholder="Date range"
          buttonTestId="school-students-filter-date-range"
        />
        <TableFilterResetButton show={true} onClick={handleResetFilters} />
      </>
    ),
    [
      userQuery,
      setUserQuery,
      selectedGrades,
      setSelectedGrades,
      selectedStatuses,
      setSelectedStatuses,
      classTimeOptions,
      selectedClassTimeIds,
      setSelectedClassTimeIds,
      classroomTeacherOptions,
      selectedClassroomTeachers,
      setSelectedClassroomTeachers,
      attendedStartDate,
      attendedEndDate,
      setAttendedDateRange,
      handleResetFilters,
    ],
  );

  const topBarRightComponents = (
    <Button
      className="ml-auto h-8"
      disabled={isPending || !!error || isDownloadingCsv}
      onClick={handleDownloadCsv}
      data-testid="school-students-download-csv"
    >
      Download as CSV
      {isDownloadingCsv ? <LoaderIcon className="ml-2 h-4 w-4 animate-spin" /> : null}
    </Button>
  );

  return (
    <EntityTable
      table={table}
      showPagination
      isLoading={isPending}
      hasError={!!error}
      emptyState="No students found"
      errorState={<TableErrorState refetchAction={refetch} />}
      filters={{ filterLeftComponents }}
      filterLeftClassName="flex-wrap"
      topBarRightComponents={topBarRightComponents}
      headerClassName="bg-[#F3F4F6]"
      paginationLeftComponents={
        <span className="text-sm font-medium text-foreground" data-testid="school-students-total">
          Total students: {data?.total ?? 0}
        </span>
      }
    />
  );
}
